/**
 * Apply shared tier and criterion focus filters to protocol DAG entities.
 */

import type {
  ProtocolAtom,
  ProtocolConnection,
  ProtocolCriterion,
  ProtocolGate,
} from "../../api/clinicalTrialsApiTypes";
import { PROTOCOL_TIERS } from "./ProtocolTierFilter";

export type FilteredProtocolGraph = {
  criteria: ProtocolCriterion[];
  atoms: ProtocolAtom[];
  gates: ProtocolGate[];
  connections: ProtocolConnection[];
};

/**
 * Filter protocol graph entities by selected tiers.
 *
 * Criteria without matching atoms are removed. Connections that reference
 * removed atoms or gates are dropped. Criterion-level connections remain when
 * the criterion is still visible.
 *
 * @param criteria - Draft criteria
 * @param atoms - Draft atoms
 * @param gates - Draft gates
 * @param connections - Draft connections
 * @param selectedTiers - Shared tier selection
 * @returns Filtered graph entities for DAG rendering
 */
export function filterProtocolGraphByTiers(
  criteria: ProtocolCriterion[],
  atoms: ProtocolAtom[],
  gates: ProtocolGate[],
  connections: ProtocolConnection[],
  selectedTiers: string[],
): FilteredProtocolGraph {
  const tiers =
    selectedTiers.length === 0 ? [...PROTOCOL_TIERS] : selectedTiers;
  const filteredAtoms = atoms.filter((atom) => tiers.includes(atom.tier));
  const visibleCriterionIds = new Set(
    filteredAtoms.map((atom) => atom.criterion_id),
  );
  const filteredCriteria = criteria.filter((criterion) =>
    visibleCriterionIds.has(criterion.criterion_id),
  );
  const filteredGates = gates.filter((gate) =>
    visibleCriterionIds.has(gate.criterion_id),
  );
  const visibleNodeIds = new Set<string>([
    ...filteredCriteria.map((criterion) => criterion.criterion_id),
    ...filteredAtoms.map((atom) => atom.atom_id),
    ...filteredGates.map((gate) => gate.gate_id),
  ]);
  const filteredConnections = connections.filter(
    (connection) =>
      visibleNodeIds.has(connection.from_node_id) &&
      visibleNodeIds.has(connection.to_node_id),
  );

  return {
    criteria: filteredCriteria,
    atoms: filteredAtoms,
    gates: filteredGates,
    connections: filteredConnections,
  };
}
